import { useState } from 'react';
import { Anchor, Group, Loader, Stack, Table, Text, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import type { SiloReadingResponse } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { ConfirmButton } from '../../../components/ConfirmButton';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { useReverseSiloReading, useSiloLocations, useSiloReadings } from '../api';
import dayjs from '../dayjs';
import { formatTonnes } from '../utils';
import { VarianceBadge } from './VarianceBadge';

/** Row action for a POSTED reading: reverse it (POST /silo/readings/:id/reverse,
 * services/api modules/silo/readings.ts). Role gate matches the API's
 * requireRole('OPERATIONS', 'ADMIN'); the consequence message is tailored to
 * whether the reading actually posted a RECONCILIATION adjustment (a
 * zero-variance reading posted no movement, so its reversal is a pure status
 * flip). */
function ReadingRowActions({ reading, siloName }: { reading: SiloReadingResponse; siloName: string }) {
  const canOperate = useCanOperate();
  const reverse = useReverseSiloReading(reading.id);
  const [error, setError] = useState<string | null>(null);

  async function handleReverse() {
    setError(null);
    try {
      await reverse.mutateAsync();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to reverse this reading');
    }
  }

  if (reading.status !== 'POSTED') return null;

  return (
    <Stack gap={2}>
      <Tooltip label="Requires Operations role" disabled={canOperate}>
        <span>
          <ConfirmButton
            size="xs"
            color="red"
            variant="light"
            disabled={!canOperate}
            loading={reverse.isPending}
            label="Reverse"
            message={
              reading.adjustmentMovementId
                ? `Reverse the ${siloName} reading from ${dayjs(reading.readAt).format('DD MMM HH:mm')}? Its reconciliation adjustment will be backed out by a reversing ledger entry, restoring the ledger quantity it overwrote.`
                : `Reverse the ${siloName} reading from ${dayjs(reading.readAt).format('DD MMM HH:mm')}? It posted no ledger movement (zero variance), so it will just be marked REVERSED.`
            }
            onConfirm={handleReverse}
          />
        </span>
      </Tooltip>
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
}

/** Register of recent silo readings (BUILD_SPEC.md §11: "registers with fast
 * fuzzy search + filters" — this module's slice of that pattern). DRAFT rows
 * (calculated but not yet posted) link back into the guided flow to resume
 * reconciliation; POSTED rows are still openable read-only for reference and
 * offer Reverse (§4.1 parity: every POSTED document reverses); REVERSED rows
 * are terminal, read-only. */
export function SiloReadingsRegister() {
  const readingsQuery = useSiloReadings({ limit: 20 });
  const locationsQuery = useSiloLocations();
  const locationsById = new Map((locationsQuery.data?.items ?? []).map((l) => [l.id, l]));

  if (readingsQuery.isLoading) {
    return (
      <Group>
        <Loader size="sm" />
        <Text c="dimmed">Loading readings…</Text>
      </Group>
    );
  }

  const readings = readingsQuery.data?.items ?? [];
  if (readings.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        No silo readings recorded yet.
      </Text>
    );
  }

  return (
    <Table.ScrollContainer minWidth={720}>
      <Table verticalSpacing="xs" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Silo</Table.Th>
            <Table.Th>Read at</Table.Th>
            <Table.Th>Level</Table.Th>
            <Table.Th>Estimated</Table.Th>
            <Table.Th>Variance</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {readings.map((r) => {
            const siloName = locationsById.get(r.locationId)?.name ?? r.locationId;
            return (
              <Table.Tr key={r.id}>
                <Table.Td>{siloName}</Table.Td>
                <Table.Td>{dayjs(r.readAt).format('DD MMM HH:mm')}</Table.Td>
                <Table.Td>
                  {r.levelValue}
                  {r.levelUnit === 'PCT' ? '%' : 'm'}
                </Table.Td>
                <Table.Td>{formatTonnes(r.estMassKg)}</Table.Td>
                <Table.Td>
                  <VarianceBadge varianceKg={r.varianceKg} variancePct={r.variancePct} />
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <DocStatusBadge status={r.status} />
                    {/* A bare `Link` (not Mantine's `component={Link}` polymorphism) so
                     * TanStack Router's `search` type is inferred correctly — see
                     * SiloStatusCard.tsx's header comment for why. */}
                    <Link to="/silo-counts/new-reading" search={{ readingId: r.id }}>
                      <Anchor component="span" size="sm">
                        {r.status === 'DRAFT' ? 'Review' : 'View'}
                      </Anchor>
                    </Link>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <ReadingRowActions reading={r} siloName={siloName} />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
